import type { PDFDocument } from "@pdfking/pdfium-wrapper"
import type { Bytes } from "@pdfking/tfunc"
import { IterableToArray, ToBlob } from "@pdfking/tfunc"

import { EngineError } from "../errors/EngineError"
import { FileContentType } from "../methods/FileContentType"
import type { PdfEngine } from "../types/engine/PdfEngine"
import type { PdfEngineOptions } from "../types/engine/PdfEngineOptions"
import { ErrorName } from "../types/errors/ErrorName"
import { ContentType } from "../types/files/ContentType"
import type { VirtualFile } from "../types/files/VirtualFile"
import type { TrackingCallback } from "../types/tasks/tracking/TrackingCallback"
import { RequestBlob } from "./RequestBlob"
import { ResizeBlobs } from "./ResizeBlobs"

export async function FetchPdfium(
  engine: PdfEngine,
  options: PdfEngineOptions,
  file: VirtualFile,
  emit: TrackingCallback
): Promise<PDFDocument> {
  const { PdfDocumentReader } = await import("@pdfking/pdfium-wrapper")

  const { chunks, size } = await RequestBlob(engine, file, emit)
  const type = FileContentType(file)

  switch (type) {
    case ContentType.PDF:
      return await PdfDocumentReader({
        stream: ResizeBlobs(chunks, options.chunk_size),
        chunk_size: options.chunk_size,
        size,
      })
    case ContentType.PNG:
    case ContentType.JPEG:
      const { ImageToPdf } = await import("@pdfking/renderer")
      const blob = await IterableToArray(chunks)
        .then(ToBlob)
        .then(b => ImageToPdf(b, options.image_resolution))

      return await PdfDocumentReader({
        stream: [blob],
        chunk_size: blob.size as Bytes,
        size: blob.size as Bytes,
      })
    default:
      throw EngineError(ErrorName.FILE_NOT_PDF)
  }
}
